import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, CalendarCheck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS_DE = {
  Monday: "Montag", Tuesday: "Dienstag", Wednesday: "Mittwoch",
  Thursday: "Donnerstag", Friday: "Freitag", Saturday: "Samstag", Sunday: "Sonntag"
};

const MONTHS_DE = {
  January: "Januar", February: "Februar", March: "März", April: "April",
  May: "Mai", June: "Juni", July: "Juli", August: "August",
  September: "September", October: "Oktober", November: "November", December: "Dezember"
};

export const HeroSection = () => {
  const [nextSlot, setNextSlot] = useState(null);
  const [stats, setStats] = useState({ stat1_value: "5+", stat1_label: "Jahre Erfahrung", stat2_value: "20+", stat2_label: "Projekte" });

  useEffect(() => {
    axios.get(`${API}/calendar/next-available`)
      .then(res => { if (res.data.available) setNextSlot(res.data); })
      .catch(() => {});
    axios.get(`${API}/company-settings`)
      .then(res => { if (res.data?.stats) setStats(s => ({ ...s, ...res.data.stats })); })
      .catch(() => {});
  }, []);

  const formatNextSlot = () => {
    if (!nextSlot) return "Bald verfügbar";
    const wd = WEEKDAYS_DE[nextSlot.weekday] || nextSlot.weekday;
    // Parse formatted date and translate month
    let formatted = nextSlot.formatted;
    Object.entries(MONTHS_DE).forEach(([en, de]) => { formatted = formatted.replace(en, de); });
    return `${wd}, ${formatted}`;
  };
  const scrollToServices = () => {
    const element = document.querySelector("#services");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToScheduler = () => {
    const element = document.querySelector("#scheduler");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      className="relative min-h-screen flex items-center pt-20"
      data-testid="hero-section">

      {/* Background */}
      <div className="absolute inset-0 bg-lab-bg-light" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}>

              <span className="label-text" data-testid="hero-label">
                Beratung für digitale Transformation
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-semibold text-lab-primary leading-tight tracking-tight"
              data-testid="hero-title">

              Prozesse optimieren.
              <br />
              <span className="text-lab-accent">Digital transformieren.</span>
              <br />
              KI integrieren.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lab-text-muted text-base lg:text-lg leading-relaxed max-w-xl"
              data-testid="hero-description">LAB336 begleitet Schweizer Unternehmen auf dem Weg zur Digitalisierung. Von der Strategieentwicklung bis zur Implementierung von KI-Lösungen – wir machen Ihre Prozesse zukunftsfähig.





            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4">

              <button
                onClick={scrollToScheduler}
                className="btn-primary rounded-none flex items-center justify-center gap-2 group"
                data-testid="hero-cta-primary">

                Beratungstermin vereinbaren
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform duration-200" />

              </button>
              <button
                onClick={scrollToServices}
                className="btn-secondary rounded-none"
                data-testid="hero-cta-secondary">

                Unsere Leistungen
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex gap-12 pt-8 border-t border-lab-border">

              <div>
                <p className="font-playfair text-3xl font-semibold text-lab-primary">
                  {stats.stat1_value}
                </p>
                <p className="text-lab-text-muted text-sm mt-1">
                  {stats.stat1_label}
                </p>
              </div>
              <div>
                <p className="font-playfair text-3xl font-semibold text-lab-primary">
                  {stats.stat2_value}
                </p>
                <p className="text-lab-text-muted text-sm mt-1">
                  {stats.stat2_label}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative hidden lg:block">

            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1641060272821-df59e2c0b5ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc0MzM2MzI3fDA&ixlib=rb-4.1.0&q=85"
                alt="Modern office architecture"
                className="w-full h-full object-cover"
                data-testid="hero-image" />

              {/* Overlay accent */}
              <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-lab-primary/20 to-transparent" />
            </div>
            {/* Floating card */}
            <div className="absolute -bottom-8 -left-8 bg-white p-6 shadow-xl max-w-xs">
              <p className="label-text mb-2 flex items-center gap-1.5">
                {nextSlot && <CalendarCheck className="w-3.5 h-3.5" />}
                Nächster freier Termin
              </p>
              <p className="font-playfair text-xl text-lab-primary">
                {formatNextSlot()}
              </p>
              {nextSlot && (
                <p className="text-sm text-lab-text-muted mt-1">ab {nextSlot.time} Uhr</p>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2">

        <button
          onClick={scrollToServices}
          className="flex flex-col items-center text-lab-text-muted hover:text-lab-accent transition-colors duration-200"
          data-testid="scroll-indicator">

          <span className="text-xs tracking-widest uppercase mb-2">Entdecken</span>
          <ChevronDown size={20} className="animate-bounce" />
        </button>
      </motion.div>
    </section>);

};

export default HeroSection;